import {
  configuredDropcursor,
  configuredTypography,
} from '@/components/tiptap/sharedExtensions/shared-extension-configs';
import Blockquote from '@tiptap/extension-blockquote';
import Bold from '@tiptap/extension-bold';
import Code from '@tiptap/extension-code';
import CodeBlock from '@tiptap/extension-code-block';
import Document from '@tiptap/extension-document';
import Gapcursor from '@tiptap/extension-gapcursor';
import Heading, { type Level } from '@tiptap/extension-heading';
import History from '@tiptap/extension-history';
import HorizontalRule from '@tiptap/extension-horizontal-rule';
import Italic from '@tiptap/extension-italic';
import Link from '@tiptap/extension-link';
import { BulletList, ListItem, ListKeymap, OrderedList } from '@tiptap/extension-list';
import Paragraph from '@tiptap/extension-paragraph';
import Placeholder, { type PlaceholderOptions } from '@tiptap/extension-placeholder';
import Strike from '@tiptap/extension-strike';
import { Table, TableCell, TableHeader, TableRow } from '@tiptap/extension-table';
import Text from '@tiptap/extension-text';
import Underline from '@tiptap/extension-underline';
import { Markdown, type MarkdownExtensionOptions } from '@tiptap/markdown';
import { textblockTypeInputRule, type AnyExtension } from '@tiptap/react';
import { createHorizontalRuleNodeView } from '../shared/horizontal-rule-node-view';
import { documentMarkdownSpec } from './document-markdown';
import { MarkdownHardBreak } from './hard-break-line-normalization';
import { listItemMarkdownSpec } from './list-item-markdown';
import { paragraphMarkdownSpec } from './paragraph-markdown';
import { trackSectionCreated } from './section-node/section-analytics';
import { getSectionEditorContext } from './section-node/section-editor-context';
import { createMarkdownNativeMarked } from './section-node/section-markdown';
import { MarkdownSectionNode } from './section-node/section-node';
import { tableMarkdownSpec, withCellAlign } from './table-markdown';

/**
 * Markdown pinned to the kit's own marked instance (ATX-only headings plus the
 * html-block section boundary), fresh per editor so the overrides and node
 * tokens stay off the global singleton. Unpinned, `@tiptap/markdown` lexes
 * with its own nested marked copy (v15 under pnpm) that never sees either
 * override. The cast bridges the app's `Marked` (v16 class instance) to the
 * option's global-callable type (tiptap's bundled v15); only instance members
 * are used at runtime.
 */
export const buildMarkdownNativeMarkdown = () =>
  Markdown.configure({
    marked: createMarkdownNativeMarked() as unknown as MarkdownExtensionOptions['marked'],
  });

// Levels that exist in the schema. Level 1 is kept because the section heading
// (created by `MarkdownSectionNode`'s input rule) is a `heading` with level 1 —
// but it is never created as an inline heading by the user.
const HEADING_LEVELS: Level[] = [1, 2, 3, 4, 5];
// Levels the user can create inline. `# ` (H1) is reserved as the section
// delimiter, so it is excluded here.
const INLINE_HEADING_LEVELS: Level[] = [2, 3, 4, 5];

/**
 * Heading rendering H2–H5 only. `#` (H1) is the section delimiter — its
 * `# ` input rule lives on `MarkdownSectionNode`, so the default `# `→H1 rule
 * and `Mod-Alt-1` shortcut are dropped here. Each inline level matches an exact
 * hash count: Heading's default `#{1,n}` rule would let `# ` create an H2 once
 * the H1 rule is gone, so `^(#{2})\s$` etc. keep `# ` falling through to the
 * section rule.
 */
const markdownNativeHeading = Heading.configure({ levels: HEADING_LEVELS }).extend({
  addInputRules() {
    return INLINE_HEADING_LEVELS.map((level) =>
      textblockTypeInputRule({
        find: new RegExp(`^(#{${level}})\\s$`),
        type: this.type,
        getAttributes: { level },
      })
    );
  },
  addKeyboardShortcuts() {
    return Object.fromEntries(
      INLINE_HEADING_LEVELS.map((level) => [
        `Mod-Alt-${level}`,
        () => this.editor.commands.toggleHeading({ level }),
      ])
    );
  },
});

/**
 * Code block with every direct authoring path removed — notes are clinical
 * prose, so a user must not be able to write one. The node stays registered so
 * stored fences and four-space-indented content still load, render and
 * round-trip (marked lexes both as code; the section boundary depends on it) —
 * so pasted or loaded markdown that lexes as code still becomes one, by
 * design.
 *
 * Only `Mod-Alt-c` is dropped from the parent shortcuts — `Backspace`, `Enter`,
 * `ArrowDown` and `Tab` exit or indent an existing block, and dropping those
 * would trap the cursor inside one. The parent's `codeBlockVSCodeHandler`
 * plugin pastes Monaco-family copies as typed code blocks; the clipboard
 * extension routes those through the markdown parser instead. It is dropped by
 * key so a plugin added by an upstream upgrade survives and shows up in the
 * runtime snapshot rather than vanishing.
 */
const readOnlyCodeBlock = CodeBlock.extend({
  addInputRules: () => [],
  addKeyboardShortcuts() {
    const shortcuts = { ...this.parent?.() };
    delete shortcuts['Mod-Alt-c'];
    return shortcuts;
  },
  addProseMirrorPlugins() {
    return (this.parent?.() ?? []).filter(
      (plugin) =>
        !String((plugin as { key?: unknown }).key).startsWith('codeBlockVSCodeHandler')
    );
  },
});

/**
 * The stock `@tiptap/extension-underline` serializes the mark as the
 * markdown-it-ins token `++text++`, which no consumer here renders as
 * underline (`marked`, Notion, Word, Google Docs, mobile clients). The kit
 * overrides serialization to emit `<u>...</u>` — legal raw HTML in markdown,
 * the form vMarkdown already stores, and the exact value every inline HTML
 * renderer underlines. The inherited `++` tokenizer is kept so notes saved
 * in the token form still hydrate to the mark; the next save converges them
 * on `<u>`.
 */
const underlineMarkdown = Underline.extend({
  renderMarkdown: (node, helpers) => `<u>${helpers.renderChildren(node)}</u>`,
});

/**
 * Table-stakes shell kit. The only md↔doc boundary is `@tiptap/markdown`
 * (hydrate via `setContent(…, { contentType: 'markdown' })`, flatten via
 * `getMarkdown()`). App-only plugins (slash, dictation, table menu, clipboard)
 * layer on at the editor level, not in this base kit. Mirrors the round-trip
 * harness kit.
 *
 * These were previously bundled via `StarterKit`; they are listed individually
 * so the kit's contents are explicit. `TrailingNode` is intentionally omitted —
 * a trailing empty paragraph breaks the serialize→parse fixed point; do not add
 * it. `# ` is the section delimiter; H2–H5 render as headings.
 */
export const buildMarkdownNativeExtensions = ({
  placeholder,
  showPlaceholderWhenDisabled = false,
}: {
  placeholder: PlaceholderOptions['placeholder'];
  showPlaceholderWhenDisabled?: boolean;
}): AnyExtension[] => [
  buildMarkdownNativeMarkdown(),
  // Core schema. Paragraph splits soft `\n` line breaks into sibling
  // paragraphs on parse (vMarkdown parity) so they survive editing.
  // Document carries the doc-level markdown render that emits the section-close
  // marker between a section and a following top-level block (SCR-6087).
  Document.extend(documentMarkdownSpec),
  Paragraph.extend(paragraphMarkdownSpec),
  Text,
  // Marks.
  Bold,
  Code,
  Italic,
  Strike,
  underlineMarkdown,
  Link.configure({ openOnClick: false }),
  // Block nodes. ListItem keeps a loose item's sibling paragraphs as real
  // paragraphs across the markdown round trip.
  Blockquote,
  BulletList,
  OrderedList,
  ListItem.extend(listItemMarkdownSpec),
  readOnlyCodeBlock,
  // Normalized `setHardBreak`: a press that would create a
  // break-only line becomes empty-paragraph structure instead of an
  // unrepresentable hardBreak run.
  MarkdownHardBreak,
  // Wrap the `<hr>` in a `[data-type='horizontalRule']` div so it picks up the
  // shared stylesheet's tall, clickable hit area — the bare stock `<hr>` is a
  // 1px line that can't be clicked to select.
  HorizontalRule.extend({
    addNodeView() {
      return createHorizontalRuleNodeView(this.name);
    },
  }),
  // Editing behaviour. `markdownNativeHeading` and `configuredDropcursor`
  // below stand in for StarterKit's Heading and Dropcursor.
  ListKeymap,
  Gapcursor,
  History,
  markdownNativeHeading,
  configuredDropcursor,
  configuredTypography,
  Placeholder.configure({
    placeholder,
    showOnlyCurrent: false,
    showOnlyWhenEditable: !showPlaceholderWhenDisabled,
  }),
  // Section analytics stay app-side (OpenSpec monorepo-kit-editor task 4.2):
  // the kernel node reports its `# ` conversions through this option; the
  // adapter resolves `editor_context` from the node's storage at emit time.
  MarkdownSectionNode.configure({
    onSectionCreated: (creationMethod, editor) =>
      trackSectionCreated(getSectionEditorContext(editor), creationMethod),
  }),
  Table.extend({ draggable: true, ...tableMarkdownSpec }).configure({ resizable: true }),
  TableRow,
  withCellAlign(TableHeader),
  withCellAlign(TableCell),
];
